// Learn audio session (issue #3473): the one Web Audio context for the Learn exercises, the sounds
// they schedule on it (metronome click, woodblock-style percussion, piano notes from the soundfont)
// and the tap clock that lets a singer's taps be compared with those sounds.
//
// Production rule (issue #3747): one AudioContext per page, created on first use, resumed from the
// first gesture and then left running for as long as the page lives, rendering silence whenever
// nothing is scheduled. It is never closed (WebKit plays quietly for the rest of the process once
// the page lets its audio go fully idle before capturing again, docs/learn/audio-research.md, iOS,
// 2026-09-04) and never suspended: on the iPhone, with a session type declared, suspending and
// resuming it left the page's output about half a second late for the rest of the app's life, the
// audio already rendered when it was suspended playing first when it resumed (2026-10-02, "later"
// and "evening"), and a build that never suspended was never late. So "at rest" means nothing
// scheduled and the page audio session rested, with the context running on: `stop()` rests the
// session and `start()` wakes it. Later exercises (Pitch Match, the Range Finder) reuse this
// session and open and end their mic tracks on top of it.
//
// What that costs: a running context keeps the app's audio session active behind a hidden page,
// which the app already does for a song, and an exercise without a microphone keeps its timeline
// running behind a hidden page, muted, until each exercise pauses itself (issue #3747).
//
// Timing. Everything audible is scheduled on the audio clock (`AudioContext.currentTime`, in
// seconds; this module works in ms) from the timelines in common/src/rhythm_echo.ts. A tap arrives
// as a pointer or key event on the main thread, whose natural clock is `performance.now()` (the
// event's own `timeStamp` is on that clock too); the audio clock only advances a render quantum at
// a time and is not what the event carries. So a tap is stamped on the performance clock and mapped
// onto the audio clock through a sampled pair of readings taken from both clocks at the same moment:
//
//   audioMs = perfMs - (perfAtSample - audioAtSample)
//
// The pair is refreshed whenever a pattern, or a bar of a warm-up's clicks, is scheduled, so drift
// between the two clocks never accumulates over a session. `getOutputTimestamp()` supplies the pair where the
// browser has it (the audio that was leaving the device at a given performance time, which already
// discounts the output latency); otherwise the pair is (`performance.now()`, `currentTime`).
//
// An output timestamp is trusted only when it is fresh: both its times non-zero, its performance
// time no earlier than the moment this session last resumed a context that was not running, and
// what it implies possible. While a context is not running (interrupted by the system, or
// suspended by the page as it once was) the audio clock stands still and the performance clock runs
// on, and right after a resume the browser can still report the pair from before the pause (iPhone
// 12 mini, store wrapper, Rhythm Echo across a lock, when the page still suspended its context):
// mapped through it, a tap on Go was drawn ahead by the whole time the phone had been locked, and the stale pair
// read as no lag at all. A resume the browser makes by itself (after a call banner, Siri or an alarm
// interrupted a visible page, or an iPhone page left alone with its mic, see "Hidden pages") never
// passes through this session, so the pair is also judged by what it says: carried
// forward to now, it must not put the sound leaving the speaker ahead of the clock by more than
// outputAheadToleranceMs, because the output cannot be ahead of the clock. A resume of a context
// that was already running (the openMic() after Pitch Match's Start) is no pause at all and leaves
// the freshness mark where it was. A timestamp that is not fresh gives way to the plain
// pair, marked provisional so the next tap or lag reading samples again, as for the zeros Chrome on
// Android reports after a resume (issue #3710).
//
// What the mapping cannot know is how long the device takes between the sound leaving the audio
// graph and the singer's tap reaching the page (speaker latency, touch latency; 340 ms measured on
// an Android phone). That is the calibration offset: measured once per device by the warm-up bar
// that opens a first session, which clicks until four of the singer's taps agree and stores their
// median (warmUpProgress in rhythm_echo.ts, issue #3523), under LocalStorageKey.RhythmCalibration,
// which is deliberately not one of the user-prefixed keys because it belongs to the device, not to
// the household member. `alignedTapMs()` applies it, and that is the time the scoring compares with
// the expected onsets. A sung note has an offset of its own, measured the same way by Sing Rhythm's
// warm-up and kept beside it (issue #3723): there the delay is the speaker's plus the microphone's.
//
// Capture (issue #3481). Exercises that listen open the microphone through this session, so the
// mic track sits on the same context as the guide sounds: `openMic()` resumes the context and
// opens the track, worklet and analysis worker (learn_mic_capture.ts), `onFrame()` delivers each
// analysed frame, `setTargetMidi()` aims the known-target estimator at the note the singer is
// meant to sing, and `endMic()` ends the track when the exercise is over. `stop()` never ends the
// mic on its own and `endMic()` never rests the session: `leave()` is the one call that does
// the whole teardown an exercise needs when it is done or gone. `setCaptureEnabled()` keeps the last
// 30 s of the raw signal so a moment can be saved after the fact (issue #3587).
//
// Guide bus (issue #3708). Everything this session makes audible - the click, the percussion, the
// lock chime, the piano notes and a recording played back (issue #3722) - goes through one
// unity-gain bus, made with the context, and only the bus reaches the speakers. Whatever the speaker
// plays echoes into the microphone, so the bus is the complete guide reference: the capture feeds it
// to the worklet's second input next to the microphone (learn_mic_capture.ts), and a saved capture
// carries it as its second channel. Nothing may bypass it, and ending the mic never disconnects it
// from the speakers: the guide plays on.
//
// Audio session (issue #3708). What the page declares to the operating system is the page audio
// session's business (client/src/audio/audio_session.ts, which keeps the measurements): 'playback'
// while the page plays without a microphone, which plays through the iPhone's silent switch and
// keeps rendering in the background, and 'play-and-record' while any microphone hold exists
// ('playback' refuses the microphone, and under 'auto' iOS could play the guide quieter). This
// session only tells it what is
// happening. `start()` activates it, synchronously before the context is created or resumed;
// `openMic()` takes a microphone hold before anything but pausing a song (see "The Player"), so the
// type has left 'playback' before the context is created (on a first open) and before
// getUserMedia; `endMic()` and a rejected open release that hold, which puts the type back to
// 'playback' on the context as it is (a switch on a live context did no harm on the iPhone);
// `stop()` rests it.
//
// Hidden pages. Under 'playback' the context keeps rendering in the background (measured), so an
// exercise would carry on in a pocket: guide notes playing, notes timing out as misses. So while the
// session is wanted running, a hidden page pauses what the session can pause without suspending the
// context (see the rule above): the guide bus is silenced at once, so nothing is heard behind the
// hidden page, and the page audio session rests. `wanted` stays as it is, nothing scheduled is
// cancelled, and a mic track and its hold stay. The audio clock runs on, so whatever was scheduled
// plays on unheard; pausing an exercise's own timeline is the exercise's business (issue #3747). A
// page that comes back activates the page audio session, resumes the context (nothing on a running
// one, and what brings back one the system interrupted), samples the clock again and lets the bus
// through, as `start()` does. A session put to rest in the meantime wins, and so does a page hidden
// again before the resume has landed; a resume the browser refuses still lets the bus through, so
// the exercise's next start() is heard. A start() or openMic() that finds the page already hidden
// (the Range Finder's mount-time open, after its permission query, landing behind a locked phone) is
// no gesture and resumes nothing: it leaves the page as a hide would, and the return resumes it.
//
// On the iPhone a hidden page with a microphone wanted (an openMic() not yet ended or rejected, open
// or still opening) is left alone: the hide does nothing, and the return touches neither the
// context, nor the mic, nor the bus. Under the declared 'play-and-record' iOS does not interrupt
// the page on a lock, so the guide plays on and the mic keeps capturing behind the locked phone
// until each exercise pauses itself (the follow-up in issue #3747), and after the unlock the guide is
// at full volume and the mic hears (docs/learn/audio-research.md, 2026-10-03). Every way the page
// paused and restored capture itself failed on the phone, with a quiet guide and a deaf microphone or
// with the late output above (the entries of 2026-10-02).
//
// A page left alone needs nothing from the clock: should the system interrupt it all the same (a
// call, say), the stale-timestamp checks (see "Timing") cover the resume the browser makes by itself.
// A start() on such a page resumes nothing either, unless the
// session was at rest: that page is paused like any other, because its context may be one no gesture
// has resumed yet (a first open behind the locked phone), and only the return can resume that. A mic
// let go while the page is still hidden (endMic(), or an open refused behind the locked phone) leaves
// a hidden page without a mic, which is then paused like any other - under 'playback' its guide would
// otherwise play on behind it - and woken by the return.
//
// Everywhere else the track survives a hidden page, and a fresh getUserMedia on every return would
// ask again on a browser that keeps no permission (Firefox, Safari on the Mac): there a hidden page
// keeps its mic while the bus and the page audio session go quiet and come back.
//
// The Player (issue #3708). A song can keep playing behind the app while the singer moves around
// with the Player minimised, and it would play on over an exercise: over the guide, and into the open
// microphone. So the session pauses a song the Player is playing when it goes from rest to running
// (`start()`), on every `openMic()`, and when a hidden page returns to a session still wanted
// running, which catches a song started from the lock screen meanwhile; that return pauses whatever
// song is playing, because it cannot tell a lock-screen start from any other. `openMic()` pauses
// before it takes its microphone hold, because the hold changes the session type and capture starts
// right after it, and neither should happen with a song still playing (a suspect for the quieter
// guide after an exercise had paused a song, issue #3747); the start() inside it may ask again,
// which does nothing when nothing is playing. A start() on a session already running does not pause
// again. Pausing only: nothing here resumes the song when the exercise ends.

import { midiToNoteName } from '../../../../../common/src/learn_rules.ts';
import { alignTap, RhythmInput, RhythmTimeline } from '../../../../../common/src/rhythm_echo.ts';
import { getPageAudioSession, PageAudioSession } from '../../../audio/audio_session.ts';
import { createGuideBus } from '../../../audio/guide_reference.ts';
import { getPageAudioContext } from '../../../audio/page_audio_context.ts';
import { localStorageGet, LocalStorageKey, localStorageSet } from '../../../data/client_local_storage.ts';
import { ActiveNote, loadInstrument, playNote, stopNote } from '../../../player/instrument.ts';
import { pausePagePlayer } from '../../../player/page_player.ts';
import { Device, getDevice } from '../../../util/platform.ts';
import {
  CaptureFrame,
  CaptureRecording,
  createMicCapture,
  MicCapture,
  MicCaptureOptions,
  SungOnset,
} from './learn_mic_capture.ts';

// One note of a piano run (schedulePianoNotes): its offset from the run's start, in ms, and its
// gain, a linear factor on the piano's own level: 1 when absent, so a guide that says nothing about
// loudness plays as every guide always has
export interface PianoNote {
  midi: number;
  offsetMs: number;
  durationMs: number;
  gain?: number;
}

// The gain of a guide played softly under a singer who is singing along (issue #3722): half the
// amplitude, about 6 dB down. That is plainly quieter than the guide the singer listened to, so the
// voice leads and less of the piano echoes into the microphone the take is graded from, while a
// phone speaker still carries it clearly enough to follow. Much lower (0.3 is about 10 dB, half as
// loud to the ear) and the guide gets lost under a singer on a phone speaker; any higher and it is
// hardly softer at all.
export const softGuideGain = 0.5;

export interface LearnAudioSession {
  // Creates the AudioContext on first use and resumes it. Call from a user gesture (the Start tap).
  start(): Promise<void>;
  // Cancels every sound still pending and rests the page audio session. The context runs on,
  // rendering silence: it is never suspended and never closed (see the header).
  stop(): Promise<void>;
  // Everything an exercise does when it is finished or is being left: the detector unaimed, the
  // pending sounds silenced, the mic track ended and the session put back to rest. Tearing the
  // microphone down touches a worklet, a worker and a media track, so each of those steps is
  // guarded on its own and the session is rested whatever they do: a guide note must never be left
  // sounding behind a singer who has gone. Safe before start() and with no mic open.
  leave(): void;
  // The audio clock in ms (0 before the context exists). Pages poll it on requestAnimationFrame.
  nowMs(): number;
  // The underlying context, for exercises that capture the mic; undefined until start().
  getAudioContext(): AudioContext | undefined;
  // How long a sound scheduled at nowMs() takes to leave the speaker, in ms: the context's base
  // plus output latency as the browser reports them (output latency counts as 0 where the browser
  // does not report it). Undefined before the context exists. About 13 ms on the iPhone and 240 to
  // 265 ms on the Samsungs measured for #3374, so it is what decides whether a sound played in
  // answer to a tap can sound with the tap at all (issue #3710).
  outputDelayMs(): number | undefined;
  // How far what is leaving the speaker now trails nowMs(), in ms, from the browser's output
  // timestamp: about 215 ms on the Galaxy A16. A screen that lights something "as it sounds" shows
  // nowMs() minus this, so the light comes with the sound rather than ahead of it (issue #3710).
  // 0 before the context exists and where the browser has no output timestamp.
  heardLagMs(): number;
  // A metronome click at `atMs` on the audio clock: a filtered noise tick about 20 ms long.
  scheduleClick(atMs: number): void;
  // A woodblock-style percussive hit at `atMs`: a decaying tone plus a noise transient, about 70 ms.
  schedulePercussion(atMs: number): void;
  // The lock chime at `atMs` (issue #3518): two bright decaying tones a breath apart, about 370 ms
  // in all. It may sound while the microphone is still open, so it is short and decaying, never
  // sustained.
  scheduleChime(atMs: number): void;
  // A piano note from the soundfont at `atMs` for `durationMs`, resolving with the time in ms it
  // was anchored at - which is `atMs` unless the soundfont took so long to load that `atMs` is
  // already past, in which case the note (and this answer) move to the clock's reading now, so a
  // caller that times anything against the guide times it against what it hears. Resolves without
  // a sound when the note was cancelled or the soundfont could not be loaded; the answer is then
  // the requested `atMs`, because nothing sounded for it to be anything else.
  //
  // One piano note at a time (issue #3518). Asking for one stops and forgets any piano note still
  // pending or ringing, and supersedes one still waiting on the soundfont - that earlier call then
  // resolves with the `atMs` it asked for and plays nothing. Two pianos on two notes are neither a
  // reference nor a thing to sing against, and the rule lives here rather than in the exercises so
  // that no caller has to remember it. Clicks, percussion and the chime are untouched: silencing
  // them is cancelScheduled's business, and a guide must never cut the chime of the note before it.
  schedulePianoNote(midi: number, atMs: number, durationMs: number): Promise<number>;
  // Several piano notes in a row as one guide (issue #3721): each note sounds at `atMs` plus its
  // `offsetMs` for its `durationMs` - do, a gap and a pattern's notes, or the steps that fill a leap.
  // schedulePianoNote is the one-note case of this, so everything said there holds for the whole
  // run: asking for one stops and forgets any piano note or run still pending, ringing or waiting on
  // the soundfont, and a later schedulePianoNote or schedulePianoNotes stops this whole run, the
  // notes not yet sounded included. It resolves with the time the run was really anchored at: `atMs`,
  // unless the soundfont load ran past it, in which case the whole run moves to the clock's reading
  // now, every note keeping its offset from that new start. Cancelled, superseded or without a
  // soundfont, it resolves with the requested `atMs` and plays nothing. An empty list plays nothing,
  // resolves with `atMs` and still replaces whatever piano was playing.
  schedulePianoNotes(notes: PianoNote[], atMs: number): Promise<number>;
  // Stops any piano note or run still pending, ringing or waiting on the soundfont, and a recording
  // still playing (playRecording), and nothing else: the click, the percussion and the lock chime
  // play on, the session is not rested and the microphone is not touched. For a guide the singer
  // cuts short ("Sing it back" in the middle of a pattern, Skip during a replay, Stop during "Hear
  // mine").
  stopPiano(): void;
  // Plays mono audio from memory (issue #3722): `samples` recorded at `sampleRate`, from `atMs` on
  // the audio clock - Sing in Solfège's "Hear mine", the singer's take cut from the rolling capture
  // (sliceCapture in capture_slice.ts). It goes through the guide bus like every other sound, so the
  // capture's reference channel, the hidden-page mute and the page audio session cover it, and it is
  // a guide like a piano run: asking for it stops any piano or recording playing, and a later piano
  // guide or recording stops it, as do stopPiano, cancelScheduled, stop() and leave(). It resolves
  // with the time it really started: `atMs`, or the clock's reading now when `atMs` is already past.
  // An empty recording plays nothing, resolves with `atMs` and still stops what was playing. The
  // samples are copied into an AudioBuffer of their own rate, which the context resamples when it
  // runs at another one. Nothing here touches storage or the network.
  playRecording(samples: Float32Array, sampleRate: number, atMs: number): Promise<number>;
  // Percussion at each play onset of one pattern. Nothing else sounds: there is no count-in, the
  // Ready · Set · Go! cue is silent (Ready and Set pulse over the play bar's last two beats) and the
  // tap bar, which follows the play bar directly, stays silent too (the page plays a percussion hit
  // per accepted tap as it happens). Also refreshes the tap clock mapping.
  schedulePattern(timeline: RhythmTimeline): void;
  // Percussion at arbitrary audio-clock times, for "Hear both again".
  scheduleTaps(tapsMs: number[]): void;
  // Clicks of a warm-up run (the exercise's calibration) at the given audio-clock times. The page
  // schedules one bar at a time as the run's clock reaches it (issue #3523), so a run that stops
  // being ticked - a locked run, a backgrounded tab - stops clicking within a bar, and a lock has
  // at most a bar of clicks to silence. Nothing else sounds: the taps the singer makes against the
  // clicks are sounded by the page as they land. Also refreshes the tap clock mapping, so the
  // mapping is renewed once a bar through a warm-up.
  scheduleCalibration(clicksMs: number[]): void;
  // Silences what is still to come: every sound whose start is still in the future is stopped. A
  // click or a percussion hit that has already started is left alone to finish its own decay -
  // each is at most ~80 ms long, and cutting one off mid-sound is itself an audible click, which
  // is exactly what the tap that locks a warm-up in would otherwise get. A piano note, and a
  // recording played back, is stopped wherever it is: it holds for as long as the exercise asked
  // (the Range Finder's guide note, a whole take), so leaving the page or moving on has to silence
  // one that is still sounding.
  cancelScheduled(): void;
  // The time of a tap on the audio clock in ms, from `perfMs` on the performance clock (default:
  // performance.now(); pass the event's timeStamp when there is one).
  stampTapMs(perfMs?: number): number;
  // stampTapMs shifted by the calibration offset: the time the singer meant, ready for scoring.
  alignedTapMs(perfMs?: number): number;
  // The device's stored calibration offset, or undefined before the first calibration. There is one
  // per input (issue #3723): a tap reads late by the output latency plus the touch path, a sung note
  // by the output latency plus the microphone's, and each is measured by its own warm-up.
  getCalibrationOffsetMs(input?: RhythmInput): number | undefined;
  // Stores a freshly measured calibration offset for this device.
  setCalibrationOffsetMs(offsetMs: number, input?: RhythmInput): void;

  // ---- microphone capture (issue #3481) ----

  // Pauses a song the Player is playing, then resumes the context and opens the mic track, capture
  // worklet and analysis worker on it. Call from a user gesture (the tap that follows the permission
  // explanation). Rejects with a MicOpenError naming the failure (denied, unavailable, busy,
  // unknown); resolves at once when the mic is already open.
  openMic(): Promise<void>;
  // Ends the mic track and tears the capture down. The session stays as it is (an exercise that is
  // done rests it too, which is what leave() does in one call). An open still in flight is dropped
  // with it. Safe before start() and when no mic is open.
  endMic(): void;
  isMicOpen(): boolean;
  // Every analysed frame while the mic is open, in order; returns the unsubscribe. May be called
  // before the mic (or the context) exists.
  onFrame(listener: (frame: CaptureFrame) => void): () => void;
  // Aims the known-target estimator at a note while the singer is meant to sing it; undefined
  // turns it off. Remembered across openMic calls.
  setTargetMidi(midi: number | undefined): void;

  // ---- sung onsets (issue #3723) ----

  // Turns the reporting of sung onsets on or off: the start of every note the singer sings, timed on
  // the audio thread, for Sing Rhythm, which scores when a sound starts and not its pitch.
  // Remembered like the target note, so it may be called before the mic (or the context) exists.
  setOnsetsEnabled(enabled: boolean): void;
  // Every sung onset while the mic is open and onsets are on, in order; returns the unsubscribe. The
  // time is when the sound reached the microphone, on the audio clock: not yet shifted by the voice
  // calibration offset, which is the exercise's to apply (alignTap), or to measure.
  onSungOnset(listener: (onset: SungOnset) => void): () => void;

  // ---- rolling capture (issue #3587) ----

  // The last 30 s of raw microphone audio, stamped on the audio clock, so that a flicker seen on
  // the screen can be saved after the fact, with the audio that produced it, and replayed on the
  // developer's machine (Pitch Match, on dev servers only, which is that page's choice), or so a
  // take can be played back to the singer (Sing in Solfège's "Hear mine", in every environment,
  // issue #3722). It stays in memory; nothing here stores or uploads it, and ending the mic drops it.
  //
  // Turns the capture on or off; off drops what was kept. Remembered like the target note, so it
  // may be called before the mic (or the context) exists.
  setCaptureEnabled(enabled: boolean): void;
  // A copy of the audio kept so far, oldest first, with the audio-clock time of its first sample;
  // undefined when nothing is kept. Taking it leaves the capture as it was.
  takeCapture(): CaptureRecording | undefined;
  // Whether there is any audio kept to take, without copying it: cheap enough for a page to ask on
  // every render, which is how Pitch Match decides whether to offer saving it at all
  hasCapture(): boolean;
}

export interface LearnAudioSessionOptions {
  createAudioContext?: () => AudioContext;
  performanceNow?: () => number;
  mic?: MicCaptureOptions;
  // What the page declares to the operating system about its audio; the default is the page's own
  audioSession?: PageAudioSession;
  // Whether the page is visible, and when that changes (see the header); the default is `document`
  visibility?: PageVisibility;
  // Pauses a song the Player is playing and does nothing otherwise (see the header); the default is
  // the page's Player
  pausePlayer?: () => void;
  // Which device this is; only on iOS is a hidden page with a mic left to the system (see the header)
  getDevice?: () => Device;
}

// The part of `document` the session watches to pause while the page is hidden
export interface PageVisibility {
  readonly visibilityState: DocumentVisibilityState;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
}

// ---- Sound design ----------------------------------------------------------------------------
// Short and synthesised: a sustained pure tone is exactly what the mic's echo canceller cannot
// remove (audio-research.md), and later exercises play these while the mic is open.

const clickDurationSec = 0.02;
const clickFilterHz = 4000;
const clickFilterQ = 1;
const clickGain = 0.6;

const percussionToneHz = 880;
const percussionDurationSec = 0.07;
const percussionGain = 0.9;
const percussionTransientSec = 0.012;
const percussionTransientHz = 2500;
const percussionTransientQ = 0.7;
const percussionTransientGain = 0.5;

const chimeFirstHz = 1318.5; // E6
const chimeSecondHz = 1568; // G6, a third above
const chimeSpacingSec = 0.12;
const chimeDurationSec = 0.25;
const chimeGain = 0.3;

// How far ahead of the clock an output timestamp may put the sound leaving the speaker before it is
// taken for a stale one (see "Timing" in the header). The page reads both clocks only as each render
// callback lands, which on some Android outputs is tens of ms apart, so a fresh pair carried forward
// can read a little ahead; a stale one is ahead by the whole pause, seconds at least.
const outputAheadToleranceMs = 50;

const pianoGain = 1;
const pianoReleaseSec = 0.2; // stopNote fades over an eighth of a second; keep the node a little longer
// A piano note stopped while it rings fades over this rather than being cut, which pops (issue #3721)
export const pianoStopFadeMs = 30;
const noiseBufferSec = 0.1;
const envelopeFloor = 0.001; // exponential ramps cannot reach 0

// A guide - a piano note, or a recording played back (issue #3722) - is tracked apart from the rest,
// so the one-at-a-time rule can find what it has to silence without touching a click, a percussion
// hit or the lock chime
type ScheduledSoundKind = 'guide' | 'other';

interface ScheduledSound {
  kind: ScheduledSoundKind;
  startSec: number; // on the audio clock; before this the sound has not begun
  endSec: number; // on the audio clock; the sound can be forgotten after this
  stop: (atSec: number) => void;
}

// Silences a piano note that is being stopped (stopPiano, a guide replacing it, cancelScheduled). One
// still to come is simply dropped. One already ringing - with nothing after it, and the mic open,
// since "Sing it back" or Skip cut it - fades over pianoStopFadeMs from wherever its gain is now,
// mid-ring or mid-release, and is disconnected once it is silent: a bare disconnect mid-waveform is
// an audible pop, as cancelScheduled's comment says of a cut click.
function fadeOutPianoNote(note: ActiveNote, startSec: number, stopSec: number) {
  fadeOutGain(note.gainNode, startSec, stopSec);
}

// The fade itself, for anything whose last node before the bus is a gain of its own: a piano note's,
// or a recording's (issue #3722)
function fadeOutGain(gainNode: GainNode, startSec: number, stopSec: number) {
  if (stopSec < startSec) {
    gainNode.disconnect();
    return;
  }
  const gain = gainNode.gain;
  if (typeof gain.cancelAndHoldAtTime === 'function') {
    gain.cancelAndHoldAtTime(stopSec);
  } else {
    // no cancelAndHoldAtTime (Firefox): hold the value it is at rather than the last one set
    const heldValue = gain.value;
    gain.cancelScheduledValues(stopSec);
    gain.setValueAtTime(heldValue, stopSec);
  }
  // a time constant a fifth of the fade leaves it under 1% of its level when the disconnect ends it
  gain.setTargetAtTime(0, stopSec, pianoStopFadeMs / 1000 / 5);
  setTimeout(() => gainNode.disconnect(), pianoStopFadeMs);
}

// What the page has asked the session for: audio now, or the rest an exercise leaves it in (nothing
// scheduled, the page audio session rested, the context running on)
type SessionIntent = 'running' | 'rested';

interface ClockSample {
  perfMs: number;
  audioMs: number;
  // A plain pair taken while the browser's output timestamp was not fresh yet (see "Timing" in the
  // header), so it does not discount the output latency every later sample will. Right after a
  // resume Chrome on Android reports zeros for a while, so the first pattern a Start tap schedules
  // got one of these, and its taps read about 200 ms late on the Galaxy A16 (issue #3710); and the
  // iPhone reports the pair from before a pause, which would put taps ahead by the whole pause
  // (issue #3708). stampTapMs samples again instead. Never set where the browser has no output
  // timestamp at all: there the plain pair is the mapping.
  provisional: boolean;
  lagMs: number; // heardLagMs at the moment of the sample; 0 for a plain pair
}

class WebAudioLearnSession implements LearnAudioSession {
  private context: AudioContext | undefined;
  private guideBus: GainNode | undefined; // made with the context; every sound goes through it (see the header)
  private readonly audioContextFactory: () => AudioContext;
  private readonly performanceNow: () => number;
  private readonly micOptions: MicCaptureOptions;
  private readonly audioSession: PageAudioSession;
  private readonly visibility: PageVisibility;
  private readonly pausePlayer: () => void;
  private readonly getDevice: () => Device;
  private noise: AudioBuffer | undefined;
  private pending: ScheduledSound[] = [];
  // Bumped by cancelScheduled and stopPiano, and so by every piano guide asked for, so a guide still
  // waiting on the soundfont is dropped when the exercise leaves or when a later guide supersedes it
  private generation = 0;
  private wanted: SessionIntent = 'rested'; // what the page last asked for; a resume that lands late obeys it
  private clock: ClockSample | undefined;
  // The performance clock when this session last finished resuming a context that was not running:
  // an output timestamp from before it is the stale one from before the pause (see "Timing")
  private resumedAtPerfMs = -Infinity;
  private capture: MicCapture | undefined; // created with the context, on first use
  private targetMidi: number | undefined; // set before the capture exists is applied when it is
  private captureEnabled = false; // likewise
  private onsetsEnabled = false; // likewise
  // The release of the page audio session's microphone hold taken by the current openMic() call,
  // from that call until endMic() or until its open is rejected: while it is set, the mic is wanted.
  // Each call has a release of its own, so a rejection that lands after the exercise has ended that
  // mic and asked for a fresh one releases only its own (long since released) hold, never the fresh
  // one's.
  private micRelease: (() => void) | undefined;
  private readonly frameListeners = new Set<(frame: CaptureFrame) => void>();
  private readonly onsetListeners = new Set<(onset: SungOnset) => void>();
  // A hidden page paused the session (see "Hidden pages"): the bus silenced and the page audio
  // session rested, the context running on. The return undoes it, and so do start() and stop().
  private pausedWhileHidden = false;

  constructor(options: LearnAudioSessionOptions = {}) {
    // the page's one context, shared with the Player's Web Audio path (issue #3718)
    this.audioContextFactory = options.createAudioContext ?? getPageAudioContext;
    this.performanceNow = options.performanceNow ?? (() => performance.now());
    this.micOptions = options.mic ?? {};
    this.audioSession = options.audioSession ?? getPageAudioSession();
    this.visibility = options.visibility ?? document;
    this.pausePlayer = options.pausePlayer ?? pausePagePlayer;
    this.getDevice = options.getDevice ?? getDevice;
  }

  // ---- context lifecycle --------------------------------------------------------------------

  // The visibility listener is registered once, with the one context it pauses and wakes. The
  // guide bus is made with the context, so no sound can be scheduled before it exists. The context is
  // kept before the bus is made: a browser that throws while making the bus must not cost the page
  // its one context, so the next call makes only the bus again.
  private ensureContext(): AudioContext {
    if (!this.context) {
      this.context = this.audioContextFactory();
      this.visibility.addEventListener('visibilitychange', () => this.onVisibilityChange());
    }
    if (!this.guideBus) {
      this.guideBus = createGuideBus(this.context);
    }
    return this.context;
  }

  // Where every sound connects: the guide bus, never the destination itself
  private guideOutput(): GainNode {
    this.ensureContext();
    return this.guideBus!;
  }

  // See "Hidden pages" in the header. A session at rest is left alone either way: nothing is
  // scheduled on it and its page audio session is at rest already, and coming back is no reason to
  // wake it.
  private onVisibilityChange() {
    const context = this.context;
    if (!context || this.restIsWanted()) {
      return;
    }
    if (this.pageIsHidden()) {
      if (this.pausesWhileHidden()) {
        this.pauseForHiddenPage();
      }
      return;
    }
    this.pauseSong(); // one started from the lock screen while the page was hidden
    if (!this.pausedWhileHidden) {
      return; // an iPhone page left alone with its mic: nothing was paused (see the header)
    }
    // The page audio session rests and wakes with the page, so the silent element an older iPhone
    // needs does not loop behind a hidden page. Waking happens outside a gesture; an element the
    // browser then refuses to play leaves the page as it was before the session was declared.
    this.audioSession.activate();
    this.resumeAfterHidden(context).catch((error: unknown) => {
      console.error('Learn audio session: coming back from a hidden page failed', error);
    });
  }

  private pageIsHidden(): boolean {
    return this.visibility.visibilityState === 'hidden';
  }

  // Everywhere but on the iPhone with a mic wanted, open or still opening, which is left alone
  private pausesWhileHidden(): boolean {
    return this.micRelease === undefined || this.getDevice() !== Device.iOS;
  }

  // What a hidden page looks like, applied by the visibility handler, by start() when it finds the
  // page already hidden, and when a hidden iPhone page lets its mic go; applying it twice changes
  // nothing. The context is not suspended (see the header): silencing the bus is what keeps the
  // rest of a sounding note, and whatever is still scheduled, from being heard behind the page.
  private pauseForHiddenPage() {
    this.setBusGain(0);
    this.pausedWhileHidden = true;
    this.audioSession.rest();
  }

  // A mic let go while an iPhone page that was left alone for it is still hidden: that page now
  // pauses like any other, or its guide would play on behind it under 'playback'
  private pauseHiddenPageWithoutMic() {
    if (!this.context || this.restIsWanted() || !this.pageIsHidden() || this.pausedWhileHidden) {
      return;
    }
    this.pauseForHiddenPage();
  }

  // The resume does nothing to a running context and brings back one the system interrupted. When
  // it has landed, whether or not it succeeded, the clock is sampled again and the bus let through,
  // unless a stop() has rested the session meanwhile (which let the bus through itself) or the page
  // has been hidden again (which silenced it again, or left it alone). Hidden and back again, both
  // returns may finish: the second finds the bus already through.
  private async resumeAfterHidden(context: AudioContext) {
    try {
      await this.resume(context);
    } catch (error) {
      // WebKit can refuse a resume after an interruption. The bus is let through all the same, so
      // the next start() (the exercise's next tap) is heard the moment it resumes.
      console.error('Learn audio session: resuming after a hidden page failed', error);
    }
    if (this.restIsWanted() || this.pageIsHidden()) {
      return;
    }
    this.sampleClock();
    this.restoreBus();
  }

  // At the context's current time, after cancelling whatever was scheduled on the bus: nothing else
  // schedules on it, and the sounds themselves are left as they were (see "Hidden pages")
  private setBusGain(value: number) {
    const context = this.context;
    const bus = this.guideBus;
    if (!context || !bus) {
      return;
    }
    const nowSec = context.currentTime;
    bus.gain.cancelScheduledValues(nowSec);
    bus.gain.setValueAtTime(value, nowSec);
  }

  // Lets through a bus a hidden page silenced, which ends that pause; the bus is otherwise never
  // touched
  private restoreBus() {
    if (!this.pausedWhileHidden) {
      return;
    }
    this.pausedWhileHidden = false;
    this.setBusGain(1);
  }

  // ---- microphone capture -------------------------------------------------------------------

  // The capture is bound to the context, so it is created no earlier than the context is (on the
  // first start or openMic, inside a gesture); listeners, the target note and the rolling capture's
  // switch are kept here until then. The guide bus is its reference.
  private ensureCapture(): MicCapture {
    if (!this.capture) {
      this.capture = createMicCapture(this.ensureContext(), this.guideOutput(), this.micOptions);
      this.capture.setTargetMidi(this.targetMidi);
      this.capture.setRecording(this.captureEnabled);
      this.capture.setOnsets(this.onsetsEnabled);
      this.capture.onOnset((onset) => {
        for (const listener of this.onsetListeners) {
          listener(onset);
        }
      });
      this.capture.onFrame((frame) => {
        for (const listener of this.frameListeners) {
          listener(frame);
        }
      });
    }
    return this.capture;
  }

  // getUserMedia and resume() are both called synchronously inside the caller's gesture (iOS
  // needs the resume there); the capture then waits for the permission answer, and an endMic()
  // that lands while the prompt is up ends the track as soon as it arrives. The microphone hold is
  // taken before either, and before a first context exists: iOS refuses the mic under 'playback'.
  // A playing song is paused before the hold, which changes the session type with capture right
  // behind it (see "The Player" in the header).
  openMic(): Promise<void> {
    this.pauseSong();
    const release = this.audioSession.holdMic();
    let capture: MicCapture;
    try {
      capture = this.ensureCapture();
    } catch (error) {
      release(); // no context or no capture: nothing will ever end this mic, so nothing may hold it
      throw error;
    }
    // A second call (the Range Finder's Continue tap joining the open its mount started) takes over
    // from the first; its hold was taken above, so the count never drops to zero in between
    this.micRelease?.();
    this.micRelease = release;
    const opening = capture.open().catch((error: unknown) => {
      // A rejected open (denied, busy, ...) leaves nothing to record for. A stale one finds its
      // release already called, which makes this a no-op.
      const current = this.micRelease === release;
      if (current) {
        this.micRelease = undefined;
      }
      release();
      if (current) {
        this.pauseHiddenPageWithoutMic();
      }
      throw error;
    });
    const started = this.start();
    return Promise.all([started, opening]).then(() => undefined);
  }

  endMic() {
    const release = this.micRelease;
    this.micRelease = undefined;
    this.capture?.end();
    release?.(); // after the track has ended: 'playback' and capture do not mix
    this.pauseHiddenPageWithoutMic();
  }

  isMicOpen(): boolean {
    return this.capture?.isOpen() ?? false;
  }

  onFrame(listener: (frame: CaptureFrame) => void): () => void {
    this.frameListeners.add(listener);
    return () => {
      this.frameListeners.delete(listener);
    };
  }

  setTargetMidi(midi: number | undefined) {
    this.targetMidi = midi;
    this.capture?.setTargetMidi(midi);
  }

  setOnsetsEnabled(enabled: boolean) {
    this.onsetsEnabled = enabled;
    this.capture?.setOnsets(enabled);
  }

  onSungOnset(listener: (onset: SungOnset) => void): () => void {
    this.onsetListeners.add(listener);
    return () => {
      this.onsetListeners.delete(listener);
    };
  }

  setCaptureEnabled(enabled: boolean) {
    this.captureEnabled = enabled;
    this.capture?.setRecording(enabled);
  }

  takeCapture(): CaptureRecording | undefined {
    return this.capture?.takeRecording();
  }

  hasCapture(): boolean {
    return this.capture?.hasRecording() ?? false;
  }

  async start(): Promise<void> {
    const wasRested = this.restIsWanted();
    if (wasRested) {
      this.pauseSong(); // from rest to running only (see "The Player" in the header)
    }
    this.audioSession.activate(); // before the context is created or resumed (see the header)
    const context = this.ensureContext();
    this.wanted = 'running';
    if (this.pageIsHidden()) {
      // Not a gesture, then (see "Hidden pages" in the header): nothing is resumed, the page is left
      // as a hide leaves it, and the return resumes it. A session that was at rest is paused even
      // on an iPhone left alone for its mic: its context may be one no gesture has resumed yet,
      // and only the return can resume that.
      if (wasRested || this.pausesWhileHidden()) {
        this.pauseForHiddenPage();
      }
      return;
    }
    // resume() is called synchronously inside the gesture that called start(); iOS requires it.
    // On a context already running it does nothing, and costs nothing.
    await this.resume(context);
    if (this.restIsWanted()) {
      // A stop() came before this resume landed and nothing has asked for the audio since. Chrome
      // and iOS Safari hold resume() until there is a user activation, and the very tap that leaves
      // an exercise is that activation, so a resume asked for without one (the Range Finder's
      // permission skip) can land after the exercise has gone. The stop() has already done all
      // there is to do: nothing is scheduled, the page audio session is at rest, and the context
      // runs on, rendering silence. The test is what the page wants now rather than "did a stop()
      // happen since I began": the same tap can leave one exercise and start the next, and that
      // next start() is what this late resume has delivered.
      return;
    }
    if (this.pageIsHidden()) {
      return; // hidden while the resume was pending: the hide has paused the page, or left it alone
    }
    this.sampleClock();
    // A bus a hidden page silenced is let through by the return; should that return have been cut
    // short, the exercise's next start is still heard
    this.restoreBus();
  }

  // Every resume goes through here, so the clock knows which output timestamps are stale. The
  // context's resume() is called before anything is awaited: start() needs it inside the gesture.
  // A context that was already running had no pause, and its output timestamp stays good.
  private async resume(context: AudioContext) {
    const wasRunning = context.state === 'running';
    await context.resume();
    if (!wasRunning) {
      this.resumedAtPerfMs = this.performanceNow();
    }
  }

  // A Player that throws must not cost the singer the exercise: the song plays on, the guide with it
  private pauseSong() {
    try {
      this.pausePlayer();
    } catch (error) {
      console.error('Learn audio session: pausing the Player failed', error);
    }
  }

  // Read through a call so the answer is what the field holds now, not what start() set before it
  // gave the thread away: everything this guard is for happens while the resume is pending
  private restIsWanted(): boolean {
    return this.wanted === 'rested';
  }

  async stop(): Promise<void> {
    if (!this.context) {
      return;
    }
    this.wanted = 'rested';
    this.cancelScheduled();
    this.restoreBus(); // the next start() must be heard
    this.audioSession.rest();
    // The context runs on, rendering silence: never suspended, never closed (see the header)
  }

  leave() {
    this.setTargetMidi(undefined);
    this.cancelScheduled();
    try {
      this.endMic();
    } finally {
      void this.stop();
    }
  }

  nowMs(): number {
    return this.context ? this.context.currentTime * 1000 : 0;
  }

  getAudioContext(): AudioContext | undefined {
    return this.context;
  }

  outputDelayMs(): number | undefined {
    if (!this.context) {
      return undefined;
    }
    // outputLatency is missing on some browsers
    const outputSec = typeof this.context.outputLatency === 'number' ? this.context.outputLatency : 0;
    return (this.context.baseLatency + outputSec) * 1000;
  }

  // ---- sounds ---------------------------------------------------------------------------------

  private noiseBuffer(context: AudioContext): AudioBuffer {
    if (!this.noise) {
      const length = Math.round(context.sampleRate * noiseBufferSec);
      this.noise = context.createBuffer(1, length, context.sampleRate);
      const samples = this.noise.getChannelData(0);
      for (let index = 0; index < samples.length; index += 1) {
        samples[index] = Math.random() * 2 - 1;
      }
    }
    return this.noise;
  }

  private track(sound: ScheduledSound) {
    const nowSec = this.ensureContext().currentTime;
    this.pending = this.pending.filter((pendingSound) => pendingSound.endSec > nowSec);
    this.pending.push(sound);
  }

  // A burst of white noise through a bandpass filter with an exponential decay
  private scheduleNoise(atSec: number, durationSec: number, filterHz: number, filterQ: number, gainValue: number) {
    const context = this.ensureContext();
    const source = context.createBufferSource();
    source.buffer = this.noiseBuffer(context);
    const filter = context.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = filterHz;
    filter.Q.value = filterQ;
    const gain = context.createGain();
    gain.gain.setValueAtTime(gainValue, atSec);
    gain.gain.exponentialRampToValueAtTime(envelopeFloor, atSec + durationSec);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.guideOutput());
    const endSec = atSec + durationSec + 0.01;
    source.start(atSec);
    source.stop(endSec);
    this.track({
      kind: 'other',
      startSec: atSec,
      endSec,
      stop: (stopSec) => {
        source.stop(stopSec);
        gain.disconnect();
      },
    });
  }

  scheduleClick(atMs: number) {
    this.scheduleNoise(atMs / 1000, clickDurationSec, clickFilterHz, clickFilterQ, clickGain);
  }

  schedulePercussion(atMs: number) {
    const context = this.ensureContext();
    const atSec = atMs / 1000;
    const oscillator = context.createOscillator();
    oscillator.type = 'sine';
    oscillator.frequency.value = percussionToneHz;
    const gain = context.createGain();
    gain.gain.setValueAtTime(percussionGain, atSec);
    gain.gain.exponentialRampToValueAtTime(envelopeFloor, atSec + percussionDurationSec);
    oscillator.connect(gain);
    gain.connect(this.guideOutput());
    const endSec = atSec + percussionDurationSec + 0.01;
    oscillator.start(atSec);
    oscillator.stop(endSec);
    this.track({
      kind: 'other',
      startSec: atSec,
      endSec,
      stop: (stopSec) => {
        oscillator.stop(stopSec);
        gain.disconnect();
      },
    });
    this.scheduleNoise(
      atSec,
      percussionTransientSec,
      percussionTransientHz,
      percussionTransientQ,
      percussionTransientGain,
    );
  }

  // The lock chime: E6 then G6 a breath apart, each a triangle tone decaying over a quarter of a
  // second. Sing-along mode goes straight on to the next note, so this can sound while the mic is
  // still open: it stays short and decaying, never a sustained tone the echo canceller cannot remove.
  scheduleChime(atMs: number) {
    const context = this.ensureContext();
    const atSec = atMs / 1000;
    const tones: Array<[number, number]> = [
      [atSec, chimeFirstHz],
      [atSec + chimeSpacingSec, chimeSecondHz],
    ];
    for (const [startSec, toneHz] of tones) {
      const oscillator = context.createOscillator();
      oscillator.type = 'triangle';
      oscillator.frequency.value = toneHz;
      const gain = context.createGain();
      gain.gain.setValueAtTime(chimeGain, startSec);
      gain.gain.exponentialRampToValueAtTime(envelopeFloor, startSec + chimeDurationSec);
      oscillator.connect(gain);
      gain.connect(this.guideOutput());
      const endSec = startSec + chimeDurationSec + 0.01;
      oscillator.start(startSec);
      oscillator.stop(endSec);
      this.track({
        kind: 'other',
        startSec,
        endSec,
        stop: (stopSec) => {
          oscillator.stop(stopSec);
          gain.disconnect();
        },
      });
    }
  }

  // The one-note case of a run, so the two can never drift apart
  schedulePianoNote(midi: number, atMs: number, durationMs: number): Promise<number> {
    return this.schedulePianoNotes([{ midi, offsetMs: 0, durationMs }], atMs);
  }

  async schedulePianoNotes(notes: PianoNote[], atMs: number): Promise<number> {
    const context = this.ensureContext();
    // One piano at a time (see the interface): the guide before this one stops here, whether it is
    // still pending, still ringing, or still waiting on the soundfont - the generation bump is what
    // the waiting one finds when its load finally lands.
    this.stopPiano();
    if (notes.length === 0) {
      return atMs;
    }
    const generation = this.generation;
    const loaded = await loadInstrument();
    if (generation !== this.generation) {
      return atMs; // cancelled, or superseded by a later guide, while the soundfont loaded
    }
    if (!loaded) {
      // The soundfont could not be loaded (a cold cache with no network, say). Resolve anyway,
      // without a sound: the exercise carries on and the singer simply does not hear the guide.
      // Waiting here for a load that already failed is worse - the page would sit on a note that
      // never sounds, scoring nothing and offering no way forward (issue #3518).
      return atMs;
    }
    // A load can take longer than the lead the caller left, and the Web Audio API starts a note
    // asked for in the past immediately while still stopping it at the time it was told: the note
    // would be cut short and, worse, a caller timing its exercise from `atMs` would be timing it
    // from a moment the singer never heard. So the whole guide moves to now, every note of a run
    // keeping its offset from the new start, and that start is what this resolves with (issue #3518).
    const startMs = Math.max(atMs, context.currentTime * 1000);
    for (const { midi, offsetMs, durationMs, gain = 1 } of notes) {
      const atSec = (startMs + offsetMs) / 1000;
      const endSec = atSec + durationMs / 1000;
      const note = this.playPianoNote(midi, pianoGain * gain, atSec);
      stopNote(note, endSec);
      // each note is tracked as a guide sound of its own, so stopping the guide stops all of them,
      // the ones still to come included
      this.track({
        kind: 'guide',
        startSec: atSec,
        endSec: endSec + pianoReleaseSec,
        stop: (stopSec) => fadeOutPianoNote(note, atSec, stopSec),
      });
    }
    return startMs;
  }

  // The one way this session plays a soundfont note: through the guide bus, so the note is in the
  // reference the capture records. playNote alone would default to the speakers.
  private playPianoNote(midi: number, gain: number, atSec: number): ActiveNote {
    return playNote(this.ensureContext(), midiToNoteName(midi), gain, atSec, this.guideOutput());
  }

  // The take as the singer sang it, through a gain of its own (unity: the level it was captured at)
  // so that stopping it can fade it as a piano note is faded rather than cut mid-waveform. It is
  // tracked as a guide, so the one-guide rule, stopPiano and cancelScheduled all find it. Nothing
  // waits on a load, so the start is decided at once.
  async playRecording(samples: Float32Array, sampleRate: number, atMs: number): Promise<number> {
    const context = this.ensureContext();
    this.stopPiano(); // one guide at a time, as for a piano run (see the interface)
    if (samples.length === 0) {
      return atMs; // an AudioBuffer cannot be empty, and there is nothing to hear
    }
    const buffer = context.createBuffer(1, samples.length, sampleRate);
    buffer.getChannelData(0).set(samples);
    const source = context.createBufferSource();
    source.buffer = buffer;
    const gain = context.createGain();
    gain.gain.value = 1;
    source.connect(gain);
    gain.connect(this.guideOutput());
    // a time already past would start at once and still be reported as asked for: report now instead
    const startMs = Math.max(atMs, context.currentTime * 1000);
    const startSec = startMs / 1000;
    const endSec = startSec + samples.length / sampleRate;
    source.start(startSec);
    source.onended = () => gain.disconnect();
    this.track({
      kind: 'guide',
      startSec,
      endSec,
      stop: (stopSec) => {
        // one still to come never starts; one playing runs on under the fade, then stops
        source.stop(stopSec < startSec ? stopSec : stopSec + pianoStopFadeMs / 1000);
        fadeOutGain(gain, startSec, stopSec);
      },
    });
    return startMs;
  }

  // Silences whatever guide is in the air - a piano note or run, a recording - and forgets it,
  // leaving every other pending sound alone. A sound that has already finished is not in the list to
  // begin with (track() prunes it). The generation bump drops a guide still waiting on the soundfont.
  stopPiano() {
    this.generation += 1;
    if (!this.context) {
      return;
    }
    const nowSec = this.context.currentTime;
    const others: ScheduledSound[] = [];
    for (const sound of this.pending) {
      if (sound.kind !== 'guide') {
        others.push(sound);
        continue;
      }
      try {
        sound.stop(nowSec);
      } catch {
        // a node that already ended on its own; nothing left to stop
      }
    }
    this.pending = others;
  }

  schedulePattern(timeline: RhythmTimeline) {
    this.sampleClock();
    for (const onsetMs of timeline.playOnsetsMs) {
      this.schedulePercussion(onsetMs);
    }
  }

  scheduleTaps(tapsMs: number[]) {
    for (const tapMs of tapsMs) {
      this.schedulePercussion(tapMs);
    }
  }

  scheduleCalibration(clicksMs: number[]) {
    this.sampleClock();
    for (const clickMs of clicksMs) {
      this.scheduleClick(clickMs);
    }
  }

  cancelScheduled() {
    this.generation += 1;
    if (!this.context) {
      return;
    }
    const nowSec = this.context.currentTime;
    const sounding: ScheduledSound[] = [];
    for (const sound of this.pending) {
      if (sound.startSec < nowSec && sound.kind !== 'guide') {
        sounding.push(sound); // already sounding and a few tens of ms long: let its decay finish it
        continue;
      }
      try {
        sound.stop(nowSec);
      } catch {
        // a node that already ended on its own; nothing left to stop
      }
    }
    this.pending = sounding; // track() forgets these once their end has passed
  }

  // ---- tap clock ------------------------------------------------------------------------------

  private sampleClock() {
    const context = this.ensureContext();
    const output = typeof context.getOutputTimestamp === 'function' ? context.getOutputTimestamp() : undefined;
    // Only a fresh output timestamp (see "Timing" in the header): some browsers report zeros until
    // the context has rendered, or a performance time with the context time still at 0, and a pair
    // from before a pause maps across time the audio clock never saw. The plain pair then.
    if (
      output &&
      output.contextTime !== undefined &&
      output.performanceTime !== undefined &&
      output.contextTime > 0 &&
      output.performanceTime > 0 &&
      output.performanceTime >= this.resumedAtPerfMs
    ) {
      const perfMs = this.performanceNow();
      // the context time leaving the speaker now, carried forward from the output timestamp
      const heardMs = output.contextTime * 1000 + (perfMs - output.performanceTime);
      const lagMs = context.currentTime * 1000 - heardMs;
      // The output cannot be ahead of the clock: a pair that says it is predates a pause, one the
      // browser resumed from by itself (see "Timing" in the header)
      if (lagMs >= -outputAheadToleranceMs) {
        this.clock = {
          perfMs: output.performanceTime,
          audioMs: output.contextTime * 1000,
          provisional: false,
          lagMs: Math.max(0, lagMs),
        };
        return;
      }
    }
    this.clock = {
      perfMs: this.performanceNow(),
      audioMs: context.currentTime * 1000,
      provisional: !!output,
      lagMs: 0,
    };
  }

  stampTapMs(perfMs = this.performanceNow()): number {
    // A provisional sample predates the first output timestamp (see ClockSample): try again, so a
    // tap is never stamped against a mapping that ignores the output latency the warm-up's taps
    // were measured with
    if (!this.clock || this.clock.provisional) {
      this.sampleClock();
    }
    const { perfMs: perfAtSample, audioMs: audioAtSample } = this.clock!;
    return perfMs - (perfAtSample - audioAtSample);
  }

  heardLagMs(): number {
    if (!this.context) {
      return 0;
    }
    // as in stampTapMs: a provisional sample has no lag in it yet, and the first pattern after a
    // Start tap is scheduled before the output timestamp exists
    if (!this.clock || this.clock.provisional) {
      this.sampleClock();
    }
    return this.clock!.lagMs;
  }

  alignedTapMs(perfMs?: number): number {
    return alignTap(this.stampTapMs(perfMs), this.getCalibrationOffsetMs() ?? 0);
  }

  // ---- calibration ----------------------------------------------------------------------------

  getCalibrationOffsetMs(input: RhythmInput = 'tap'): number | undefined {
    return localStorageGet(calibrationKey(input))?.offsetMs;
  }

  setCalibrationOffsetMs(offsetMs: number, input: RhythmInput = 'tap') {
    localStorageSet(calibrationKey(input), { offsetMs, measuredAt: new Date().toISOString() });
  }
}

// Where each input's calibration offset is kept: both belong to the device, not to the member
function calibrationKey(
  input: RhythmInput,
): LocalStorageKey.RhythmCalibration | LocalStorageKey.RhythmVoiceCalibration {
  return input === 'voice' ? LocalStorageKey.RhythmVoiceCalibration : LocalStorageKey.RhythmCalibration;
}

// A session of its own, for tests that inject a fake AudioContext
export function createLearnAudioSession(options: LearnAudioSessionOptions = {}): LearnAudioSession {
  return new WebAudioLearnSession(options);
}

let pageSession: LearnAudioSession | undefined;

// The page-wide session every Learn exercise shares (one AudioContext per page, see the header)
export function getLearnAudioSession(): LearnAudioSession {
  pageSession ??= new WebAudioLearnSession();
  return pageSession;
}
